import { useEffect, useState } from "react";
import Coin from "@/components/game/Coin";
import { STAT_META } from "@/components/game/CardStats";
import { LAYER } from "@/lib/terrain";

const ABSORB = STAT_META[0];

// Weather line (what the piece adds: Absorption / Shading / Storm) gets the Absorption icon; money lines get a coin.
const STAT_LINES = ["stats"];
const COIN_LINES = ["cost"];

const panel = {
  background: "var(--card)",
  borderRadius: "var(--radius)",
  boxShadow: "var(--shadow-card)",
  fontSize: 13,
  lineHeight: 1.4,
};

// Number in --sea when positive, --ink-2 otherwise.
function Value({ v }) {
  const positive = typeof v === "string" ? v.trim().startsWith("+") : Number(v) > 0;
  return <span style={{ fontWeight: 700, color: positive ? "var(--sea)" : "var(--ink-2)" }}>{v}</span>;
}

// Floating preview card beside a square (or the illegal reason), read from /build_preview.
export default function PlacementPreview({ cellKey, entry, gridCols, gridRows, showTeaching }) {
  if (!cellKey || !entry) return null;
  const [r, c] = cellKey.split(",").map(Number);
  const leftSpace = c / gridCols;
  const rightSpace = (gridCols - c - 1) / gridCols;
  const onRight = rightSpace >= leftSpace;
  const pct = Math.max(leftSpace, rightSpace) * 100;
  const below = r < gridRows / 2;
  const style = {
    ...panel,
    width: `min(240px, max(170px, calc(${pct}% - 8px)))`,
    ...(onRight
      ? { left: `calc(${((c + 1) / gridCols) * 100}% + 6px)` }
      : { right: `calc(${((gridCols - c) / gridCols) * 100}% + 6px)` }),
    ...(below ? { top: `${(r / gridRows) * 100}%` } : { bottom: `${((gridRows - r - 1) / gridRows) * 100}%` }),
  };

  if (!entry.legal) {
    return (
      <div data-testid="placement-preview-illegal" className="absolute pointer-events-none px-3 py-2" style={{ ...style, zIndex: LAYER.preview, color: "var(--danger)", fontWeight: 600 }}>
        {entry.reason}
      </div>
    );
  }

  return (
    <div data-testid="placement-preview-card" className="absolute pointer-events-none px-3 py-2.5" style={{ ...style, zIndex: LAYER.preview, color: "var(--ink-2)" }}>
      <ul className="space-y-0.5">
        {entry.lines.map((ln) => (
          <li key={ln.key} data-testid={`preview-line-${ln.key}`}>
            {STAT_LINES.includes(ln.key) && (
              <span className="inline-flex align-[-2px] mr-1">
                <ABSORB.Icon s={14} c={ABSORB.color} />
              </span>
            )}
            {COIN_LINES.includes(ln.key) && (
              <span className="inline-flex align-[-2px] mr-1">
                <Coin size={13} />
              </span>
            )}
            {ln.value !== undefined && (
              <span className="mr-1">
                <span style={{ fontWeight: 600, color: "var(--ink)" }}>{ln.label}</span> <Value v={ln.value} />
              </span>
            )}
            {ln.detail && <span style={ln.value === undefined ? { fontWeight: 600, color: "var(--ink)" } : undefined}>{ln.detail}</span>}
          </li>
        ))}
      </ul>
      {showTeaching && entry.teaching && (
        <div data-testid="preview-teaching" className="mt-1.5 pt-1.5" style={{ borderTop: "1px solid var(--line-soft)", color: "var(--ink-3)" }}>
          {entry.teaching}
        </div>
      )}
    </div>
  );
}

// Score delta that flies from the placed square into the player's score chip.
export function ScoreFly({ fly, onDone }) {
  const [go, setGo] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setGo(true));
    const t = setTimeout(onDone, 950);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [onDone]);
  const { from, to, text } = fly;
  return (
    <div
      data-testid="score-fly"
      className="fixed z-[60] pointer-events-none px-2 py-1 rounded-full"
      style={{
        left: from.x,
        top: from.y,
        background: "var(--sea)",
        color: "var(--on-sea)",
        fontSize: 12,
        fontWeight: 700,
        transform: go ? `translate(${to.x - from.x}px, ${to.y - from.y}px) scale(0.7)` : "translate(0,0) scale(1)",
        opacity: go ? 0.2 : 1,
        transition: "transform 850ms cubic-bezier(.5,0,.3,1), opacity 850ms ease-in",
      }}
    >
      {text}
    </div>
  );
}
